import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

export interface ProjectMember {
  id: number;
  project_id: number;
  user_id: number;
  permissions: string[];
  denied_permissions: string[];
  created_at: number;
  updated_at: number;
  user_display_name?: string;
  user_email?: string;
}

export interface ProjectMembersResponse {
  project_members: ProjectMember[];
}

export function useProjectMembers(projectId: number | null) {
  return useQuery({
    queryKey: ['project-members', { project_id: projectId }],
    queryFn: ({ signal }) =>
      api.get<ProjectMembersResponse>(`/api/projects/${projectId}/members`, signal),
    enabled: projectId !== null,
  });
}

export interface UpsertProjectMemberInput {
  project_id: number;
  user_id: number;
  permissions?: string[];
  denied_permissions?: string[];
}

export function useUpsertProjectMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ project_id, ...body }: UpsertProjectMemberInput) =>
      api.post<{ project_member: ProjectMember }>(
        `/api/projects/${project_id}/members`,
        body,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['project-members', { project_id: vars.project_id }] });
    },
  });
}

export function useRemoveProjectMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ project_id, member_id }: { project_id: number; member_id: number }) =>
      api.delete<void>(`/api/projects/${project_id}/members/${member_id}`),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['project-members', { project_id: vars.project_id }] });
    },
  });
}
